import { useEffect, type RefObject } from 'react';

/**
 * @aiPurpose dismiss a non-modal popover or menu when the user clicks outside
 *   it or presses Escape, while letting the underlying click reach its target.
 * @aiInvariant listeners are only attached while `enabled` is true; mousedown
 *   inside `ref` does NOT trigger `onDismiss`, so clicks on both the popover
 *   trigger and the popover panel are safe as long as both live inside `ref`.
 * @aiPrecondition `ref` is attached to a wrapper that contains BOTH the
 *   trigger and the popover panel — otherwise clicking the trigger while
 *   the popover is open will dismiss it and immediately reopen via toggle.
 */
export function useDismiss(opts: {
  ref: RefObject<HTMLElement | null>;
  enabled: boolean;
  onDismiss: () => void;
}): void {
  const { ref, enabled, onDismiss } = opts;
  useEffect(() => {
    if (!enabled) return;
    const onMouseDown = (e: MouseEvent) => {
      const node = ref.current;
      if (node && !node.contains(e.target as Node)) onDismiss();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [ref, enabled, onDismiss]);
}
